/**
 * Data Hub — domain types.
 *
 * Ported from xfactor-data-pipeline (`src/lib/data-hub/**`) and narrowed to what
 * the console actually renders. These are the contract the adapter fulfils; the
 * tab components never reach past this file for their shapes.
 */

import type { DatasetKind } from "./dataset";

export type { DatasetKind };

/* ---------------- Lifecycle ---------------- */

export type LifecycleStatus = "accepted" | "rejected";

export type StageState =
  | "staging.open"
  | "staging.deduped"
  | "staging.fixed"
  | "staging.ignored"
  | "production.promoted"
  | "production.benchmarked"
  | "production.normalized"
  | "production.summarized";

export type QcBucket =
  | "clean"
  | "fixed"
  | "open"
  | "rejected"
  | "deduped"
  | "ignored"
  | "promoted";

export const BUCKET_FILTERS = [
  "all",
  "open",
  "needs_review",
  "fixed",
  "deduped",
  "ignored",
  "promoted",
  "rejected",
] as const;
export type BucketFilter = (typeof BUCKET_FILTERS)[number];

/* ---------------- Header stats ---------------- */

/**
 * Header counts. `null` means "could not be measured" (e.g. the table does not
 * exist in this tenant) and must render as an em-dash — never as 0, and never
 * as a plausible-looking sample number.
 */
export type DataHubStats = {
  staging: number | null;
  timeseries: number | null;
  benchmarks: number | null;
  summaries: number | null;
  runs: number | null;
};

/* ---------------- Pipeline runs ---------------- */

export type PhaseKey =
  | "import"
  | "cleanse"
  | "commit"
  | "benchmark"
  | "normalize"
  | "summarize"
  | "kpi_scores"
  | "ro_scores";

export type PhaseStatus = "success" | "failed" | "skipped" | "running";

export type RunStatus = "running" | "succeeded" | "partial" | "failed";

export type PhaseResult = {
  /** A console step or an executable phase — the run records both. */
  phase: string;
  status: PhaseStatus;
  count?: number;
  error?: string | null;
  reason?: string | null;
  /** Decision counts; the only place a Columns/Accounts outcome is kept. */
  extras?: Record<string, unknown> | null;
  /** "phase_1" | "phase_2", for the run detail's section headers. */
  group?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
};

export type PipelineRun = {
  id: string;
  run_no: number;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  status: RunStatus;
  source: string | null;
  triggered_by: string | null;
  /** Fixed for the life of the run; the runs table renders it as a column. */
  dataset_kind: DatasetKind;
  phases: PhaseResult[];
};

/** Row counts per dataset, for the selector. */
export type DatasetAvailability = {
  dataset_kind: DatasetKind;
  staging: number;
  timeseries: number;
  runs: number;
  has_data: boolean;
};

/* ---------------- Staging ---------------- */

export type StagingRow = {
  id: string;
  account_id: string | null;
  account_name: string | null;
  /** Straight from the customer's file; the only account columns a fresh import fills. */
  source_account_id: string | null;
  source_account_name: string | null;
  metric_id: string | null;
  metric_name: string | null;
  category: string | null;
  measurement_date: string | null;
  value: number | string | null;
  lifecycle_status: LifecycleStatus;
  stage_state: StageState;
  pipeline_status: string | null;
  qc: { bucket?: QcBucket | null; findings?: QcFinding[] } | null;
  created_at: string | null;
};

/* ---------------- Production ---------------- */

export type ProductionRow = {
  id: string;
  measurement_date: string;
  account_name: string;
  metric_name: string;
  metric_id: string;
  category: string;
  value: number;
  normalized_value: number | null;
};

/* ---------------- QC ---------------- */

export type QcSeverity = "auto-fix" | "warn" | "hard";
export type QcGroup =
  | "resolution"
  | "hygiene"
  | "value_conformance"
  | "duplicates";

export type QcProbe = {
  id: string;
  group: QcGroup;
  label: string;
  description: string;
  severity: QcSeverity;
  default_enabled: boolean;
};

export type QcFinding = {
  probe_id: string;
  severity: QcSeverity;
  message: string;
};

export type QcReport = {
  run_id: string;
  generated_at: string;
  scope: "file" | "production";
  rows_scanned: number;
  rows_auto_fixed: number;
  rows_open: number;
  rows_rejected: number;
  by_probe: {
    probe_id: string;
    label: string;
    group: QcGroup;
    severity: QcSeverity;
    count: number;
  }[];
};

/* ---------------- Benchmarks ---------------- */

export type BenchmarkTier = 0 | 1 | 2;

export type BenchmarkMetric = {
  metric_id: string;
  metric_name: string;
  category: string;
  uom: string | null;
  higher_is_better: boolean;
  min: number;
  /**
   * p10 is stored as organization_benchmarks.benchmark_value on the
   * percentile_10 row (there is no p10 column). Null when that sibling is missing.
   */
  p10: number | null;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  max: number;
  mean: number;
  stddev: number;
  tier: BenchmarkTier | null;
  source: string;
  confidence: "high" | "medium" | "low";
  dqs: number;
  n: number;
};

/* ---------------- Matrices (normalized + summarized) ---------------- */

export type MatrixCell = {
  normalized: number | null;
  actual: number | null;
};

export type MatrixMetric = {
  metric_id: string;
  metric_name: string;
  category: string;
  uom: string | null;
};

/** Rows are dates (normalized tab) or periods (summarized tab). */
export type MatrixRow = {
  key: string;
  label: string;
  cells: Record<string, MatrixCell>;
};

export type Matrix = {
  metrics: MatrixMetric[];
  rows: MatrixRow[];
};

export type MatrixQuery = {
  axis?: "date" | "period" | "account";
  period_type?: string;
};

/* ---------------- KPI category scores ---------------- */

export type CategoryScore = {
  category_key: string;
  label: string;
  score: number | null;
  max: number;
};

export type PerKpiScore = {
  metric_id: string;
  metric_name: string;
  category_key: string;
  effective_share: number;
  n: number;
  score: number;
};

export type CategoryScores = {
  saved_at: string | null;
  category_rows: number;
  overall_rows: number;
  composite: number | null;
  categories: CategoryScore[];
  per_kpi: PerKpiScore[];
  /** Monthly buckets from the API (`period_start` ISO / period key). */
  trend: { period_start: string; composite: number | null }[];
  /** Mirrors the reference app's config-drift banner. */
  label_drift: { expected: string[]; found: string[] } | null;
};

/* ---------------- Risk & Opportunity ---------------- */

export type RoScope = "risk" | "opportunity";

export type RoDimension = {
  key: string;
  label: string;
  share: number;
  n: number;
  score: number;
  optional?: boolean;
};

export type RoQuadrant = "Q1" | "Q2" | "Q3" | "Q4";

export type RoAccountPoint = {
  account_id: string;
  account_name: string;
  rcs: number;
  ocs: number;
  quadrant: RoQuadrant;
};

export type RoScores = {
  rcs: number;
  ocs: number;
  rcs_band: string;
  ocs_band: string;
  risk_dimensions: RoDimension[];
  opportunity_dimensions: RoDimension[];
  accounts: RoAccountPoint[];
  trend: { period: string; rcs: number; ocs: number }[];
  deltas: { label: string; rcs_delta: number; ocs_delta: number }[];
  dimension_deltas: { key: string; label: string; delta: number }[];
  top_movers: { account_name: string; metric: string; delta: number }[];
};

/* ---------------- Run wizard ---------------- */

export type WizardPhaseGroup = "phase1" | "phase2" | "other";

export type WizardPhase = {
  key: PhaseKey | "reset";
  label: string;
  group: WizardPhaseGroup;
  defaultEnabled: boolean;
};
